import { FetchBaseQueryError } from '@reduxjs/toolkit/query/react'
import { AxiosError } from 'axios'
import { apiService } from '@afterpaytouch/portal-api/api'
import type { ThunkExtraArgs } from '../types'
import {
  AUTH_SESSION_HANDOFF_PATH,
  AUTH_SESSION_HEADER,
  AUTH_SESSIONS_PATH,
  AuthSessionHandoffResponse,
  CodeAttemptRequest,
  CodeAttemptResponse,
  CreateAuthSessionRequest,
  CreateAuthSessionResponse,
  CreatedAuthSession,
  OpenEmailChallengeRequest,
  OpenEmailChallengeResponse,
} from '../../model'

const tokens = new Map<string, string>()

const sessionHeader = (sessionRef: string): Record<string, string> => {
  const token = tokens.get(sessionRef)
  return token === undefined ? {} : { [AUTH_SESSION_HEADER]: token }
}

const challengesPath = (sessionRef: string): string => `${AUTH_SESSIONS_PATH}/${encodeURIComponent(sessionRef)}/challenges`

const settle = async <T>(api: { extra: unknown }, request: (extra: ThunkExtraArgs) => Promise<T>): Promise<{ data: T } | { error: FetchBaseQueryError }> => {
  try {
    return { data: await request(api.extra as ThunkExtraArgs) }
  } catch (e) {
    const { response, message } = e as AxiosError
    return { error: { status: response?.status ?? 'FETCH_ERROR', data: response?.data ?? message } as FetchBaseQueryError }
  }
}

export const authSessionsService = apiService.service.injectEndpoints({
  endpoints: (build) => ({
    createAuthSession: build.mutation<CreatedAuthSession, { checkoutToken: string; locale: string }>({
      queryFn: async ({ checkoutToken, locale }, api) =>
        await settle(api, async ({ authSessionsApi }) => {
          const body: CreateAuthSessionRequest = { context: { kind: 'CHECKOUT', checkoutToken, locale }, device: { credential: null } }
          const { session, nextStep } = await authSessionsApi().post<CreateAuthSessionRequest, CreateAuthSessionResponse>(AUTH_SESSIONS_PATH, body)
          tokens.set(session.sessionRef, session.sessionToken)
          return { sessionRef: session.sessionRef, nextStep }
        }),
    }),
    openEmailChallenge: build.mutation<OpenEmailChallengeResponse, { sessionRef: string; email: string }>({
      queryFn: async ({ sessionRef, email }, api) =>
        await settle(api, async ({ authSessionsApi }) => {
          const body: OpenEmailChallengeRequest = { factor: 'EMAIL_CODE', identifier: { kind: 'EMAIL', value: email } }
          const path = challengesPath(sessionRef)
          return await authSessionsApi().post<OpenEmailChallengeRequest, OpenEmailChallengeResponse>(path, body, sessionHeader(sessionRef))
        }),
    }),
    attemptAuthSessionCode: build.mutation<CodeAttemptResponse, { sessionRef: string; challengeRef: string; code: string }>({
      queryFn: async ({ sessionRef, challengeRef, code }, api) =>
        await settle(api, async ({ authSessionsApi }) => {
          const path = `${challengesPath(sessionRef)}/${encodeURIComponent(challengeRef)}/attempts`
          return await authSessionsApi().post<CodeAttemptRequest, CodeAttemptResponse>(path, { factor: 'EMAIL_CODE', code }, sessionHeader(sessionRef))
        }),
    }),
    handoffAuthSession: build.mutation<AuthSessionHandoffResponse, { sessionRef: string }>({
      queryFn: async ({ sessionRef }, api) => {
        const config = { headers: sessionHeader(sessionRef), 'axios-retry': { retries: 0 } }
        tokens.delete(sessionRef)
        return await settle(
          api,
          async ({ portalApi }) => (await portalApi().axiosInstance.post<AuthSessionHandoffResponse>(AUTH_SESSION_HANDOFF_PATH, {}, config)).data
        )
      },
    }),
  }),
})

export const { useCreateAuthSessionMutation, useOpenEmailChallengeMutation, useAttemptAuthSessionCodeMutation, useHandoffAuthSessionMutation } =
  authSessionsService
